import { useCallback, useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { api } from "@/api";
import { Toaster, toast } from "@/components/ui/sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import AuthPanel from "@/components/AuthPanel";
import Marketplace from "@/pages/Marketplace";
import ListingDetail from "@/pages/ListingDetail";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import OrdersPage from "@/pages/OrdersPage";
import SellerDashboard from "@/pages/SellerDashboard";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import PolicyPage from "@/pages/PolicyPage";
import { TermsGate } from "@/components/TermsGate";
import FaqPage from "@/pages/FaqPage";
import MessagesPage from "@/pages/MessagesPage";
import SellerProfilePage from "@/pages/SellerProfilePage";
import FavoritesPage from "@/pages/FavoritesPage";
import AdminFlagsPage from "@/pages/AdminFlagsPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import ProfilePage from "@/pages/ProfilePage";
import NewsFeedPage from "@/pages/NewsFeedPage";
import MemberSearchPage from "@/pages/MemberSearchPage";
import FeedThreadPage from "@/pages/FeedThreadPage";
import PlusPage from "@/pages/PlusPage";
import SellerAnalyticsPage from "@/pages/SellerAnalyticsPage";
import ProfitTrackerPage from "@/pages/ProfitTrackerPage";
import SearchPage from "@/pages/SearchPage";
import { PlusTrialBanner } from "@/components/PlusTrialBanner";
import { CartAddNotification } from "@/components/CartAddNotification";

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bonsai_cart") || "[]"); } catch { return []; }
  });
  const [notifications, setNotifications] = useState([]);
  const [cartNotification, setCartNotification] = useState(null);

  useEffect(() => { localStorage.setItem("bonsai_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  const loadNotifications = useCallback(() => {
    if (!user) return;
    api.get("/notifications").then(({ data }) => setNotifications(data)).catch(() => null);
  }, [user]);
  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  const addToCart = useCallback((listing, fulfillment = "shipping") => {
    let added = false;
    let reason = "Could not add this item to cart";
    setCart((current) => {
      const existing = current.find((item) => item.id === listing.id);
      const stock = Number(listing.stock || 0);
      if (stock < 1) { reason = "This item is out of stock"; return current; }
      if (existing) {
        if (existing.quantity >= stock) { reason = `Only ${stock} available`; return current; }
        added = true;
        return current.map((item) => item.id === listing.id ? { ...item, quantity: item.quantity + 1, fulfillment } : item);
      }
      added = true;
      return [...current, { ...listing, quantity: 1, fulfillment }];
    });
    setTimeout(() => {
      if (added) setCartNotification({ id: `${listing.id}-${Date.now()}`, title: listing.title });
      else toast.error(reason);
    }, 0);
  }, []);

  const dismissCartNotification = useCallback(() => setCartNotification(null), []);

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => null);
    setUser(null);
    setAuthLoading(false);
    toast.success("Logged out");
  }, []);
  const clearCart = useCallback(() => setCart([]), []);

  return (
    <div className="App">
      <BrowserRouter>
        <PlusTrialBanner user={user} />
        <Header user={user} cartCount={cartCount} notificationCount={notifications.filter((item) => !item.read).length} onLogout={logout} />
        <Routes>
          <Route path="/" element={<Marketplace onAddToCart={addToCart} user={user} />} />
          <Route path="/listing/:id" element={<ListingDetail onAddToCart={addToCart} user={user} />} />
          <Route path="/seller/:sellerId" element={<SellerProfilePage onAddToCart={addToCart} user={user} />} />
          <Route path="/favorites" element={<FavoritesPage user={user} authLoading={authLoading} onAddToCart={addToCart} />} />
          <Route path="/profile" element={<ProfilePage user={user} authLoading={authLoading} onUserUpdated={setUser} />} />
          <Route path="/feed" element={<NewsFeedPage user={user} authLoading={authLoading} />} />
          <Route path="/feed/:postId" element={<FeedThreadPage user={user} authLoading={authLoading} />} />
          <Route path="/members" element={<MemberSearchPage user={user} authLoading={authLoading} />} />
          <Route path="/search" element={<SearchPage user={user} />} />
          <Route path="/cart" element={<CartPage cart={cart} setCart={setCart} user={user} authLoading={authLoading} />} />
          <Route path="/checkout" element={<CheckoutPage cart={cart} user={user} authLoading={authLoading} />} />
          <Route path="/checkout/success" element={<CheckoutSuccess clearCart={clearCart} />} />
          <Route path="/orders" element={<OrdersPage user={user} authLoading={authLoading} />} />
          <Route path="/seller" element={<SellerDashboard user={user} authLoading={authLoading} />} />
          <Route path="/seller/analytics" element={<SellerAnalyticsPage user={user} authLoading={authLoading} />} />
          <Route path="/seller/profit" element={<ProfitTrackerPage user={user} authLoading={authLoading} />} />
          <Route path="/plus" element={<PlusPage user={user} authLoading={authLoading} />} />
          <Route path="/auth" element={<AuthPanel onAuth={(nextUser) => { setUser(nextUser); setAuthLoading(false); }} />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/policies/:policyKey" element={<PolicyPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/messages" element={<MessagesPage user={user} authLoading={authLoading} onNotificationsChanged={loadNotifications} />} />
          <Route path="/admin/flags" element={<AdminFlagsPage user={user} authLoading={authLoading} />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage user={user} authLoading={authLoading} />} />
        </Routes>
        <Footer user={user} />
        <CartAddNotification notification={cartNotification} onDismiss={dismissCartNotification} />
        <Toaster richColors position="bottom-right" />
        <TermsGate user={user} onAccepted={setUser} />
      </BrowserRouter>
    </div>
  );
}

export default App;
